import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Pencil, Check, X, Shuffle } from 'lucide-react';

/**
 * Small dropdown + shuffle button that lets the admin pick how a list is
 * ordered on the public site.
 *
 * Props:
 *  - kind: "categories" | "tags" | "markets" | "expos" | "festivals"
 *  - mode: current mode string
 *  - onModeChange(newMode): called with the new mode
 *  - onShuffle(): called when admin clicks "Shuffle now" (only visible in random_shuffle mode)
 *  - accent: tailwind color for the button (e.g. "bg-blue-500")
 *  - hideCustom: if true, "Custom (drag)" isn't offered (used for categories/tags)
 */
export function SortModePicker({ kind, mode, onModeChange, onShuffle, accent = 'bg-gray-700', hideCustom = false }) {
  const opts = [
    !hideCustom && { v: 'custom', label: 'Custom (drag)' },
    { v: 'az', label: 'A → Z' },
    { v: 'za', label: 'Z → A' },
    { v: 'random_visit', label: 'Random (every visit)' },
    { v: 'random_shuffle', label: 'Random (shuffle once)' },
  ].filter(Boolean);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`sort-picker-${kind}`}>
      <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Display order:</label>
      <select
        data-testid={`sort-mode-${kind}`}
        value={mode || 'custom'}
        onChange={(e) => onModeChange(e.target.value)}
        className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#252525] px-2 py-1"
      >
        {opts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      {mode === 'random_shuffle' && (
        <Button
          data-testid={`sort-shuffle-${kind}`}
          size="sm"
          onClick={onShuffle}
          className={`${accent} text-white hover:opacity-90`}
        >
          <Shuffle className="w-3.5 h-3.5 mr-1" /> Shuffle now
        </Button>
      )}
    </div>
  );
}

/**
 * Inline-editable name. Renders the name plus a small pencil icon; on click,
 * the name is swapped for an <input> + ✓ + ✕ controls.
 *
 * Props:
 *  - value: current name (string)
 *  - onSave(newName): called with the trimmed new name if changed
 *  - textClassName: className applied to the read-only <span>
 *  - testIdPrefix: used to build data-testid values for e2e
 */
export function EditableName({ value, onSave, textClassName = '', testIdPrefix = 'row' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = async () => {
    const trimmed = (draft || '').trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch (_) {
      // parent handler shows toast/message; leave the input open so user can retry
    }
  };

  if (!editing) {
    return (
      <span className={`inline-flex items-center gap-2 ${textClassName}`}>
        <span data-testid={`${testIdPrefix}-name`}>{value}</span>
        <button
          type="button"
          data-testid={`${testIdPrefix}-edit`}
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="text-gray-400 hover:text-blue-500 transition-colors"
          aria-label="Rename"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        data-testid={`${testIdPrefix}-input`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setEditing(false); setDraft(value); }
        }}
        className="h-8 text-sm"
      />
      <button
        type="button"
        data-testid={`${testIdPrefix}-save`}
        onClick={commit}
        className="text-green-600 hover:text-green-800 p-1"
        aria-label="Save"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        data-testid={`${testIdPrefix}-cancel`}
        onClick={() => { setEditing(false); setDraft(value); }}
        className="text-gray-500 hover:text-gray-700 p-1"
        aria-label="Cancel"
      >
        <X className="w-4 h-4" />
      </button>
    </span>
  );
}
